import React, { useState, useMemo, useRef, useEffect } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Chip,
  Avatar,
  TableSortLabel,
  Box,
  Typography,
  Tooltip,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

export default function ContactsTable({
  contacts,
  onEdit,
  onQuestion,
  onDelete,
  loading,
}) {
  const tableContainerRef = useRef(null)
  const [showLeftShadow, setShowLeftShadow] = useState(false)
  const [showRightShadow, setShowRightShadow] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // Check scroll position and update shadow states
  const checkScrollPosition = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        tableContainerRef.current
      const isAtRight = scrollLeft >= scrollWidth - clientWidth - 1 // Small tolerance for rounding
      setShowLeftShadow(scrollLeft > 0)
      setShowRightShadow(!isAtRight)
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(!isAtRight)
    }
  }

  // Add scroll event listener
  useEffect(() => {
    const container = tableContainerRef.current
    if (container) {
      checkScrollPosition() // Initial check
      container.addEventListener('scroll', checkScrollPosition)
      window.addEventListener('resize', checkScrollPosition)
      return () => {
        container.removeEventListener('scroll', checkScrollPosition)
        window.removeEventListener('resize', checkScrollPosition)
      }
    }
  }, [contacts]) // Re-run when contacts change

  const TYPE_VISUALS = {
    Agent: { color: '#3B82F6', icon: 'user', style: 'far' },
    Buyer: { color: '#10B981', icon: 'user', style: 'far' },
    Seller: { color: '#F59E0B', icon: 'user', style: 'far' },
    Investor: { color: '#8B5CF6', icon: 'building', style: 'far' },
    Contractor: { color: '#EF4444', icon: 'hammer', style: 'far' },
    'Property Manager': {
      color: '#06B6D4',
      icon: 'home',
      style: 'far',
    },
    'Hard Money Lender': {
      color: '#84CC16',
      icon: 'dollar-sign',
      style: 'fas',
    },
    'Insurance Agent': {
      color: '#F97316',
      icon: 'file-alt',
      style: 'far',
    },
    'Title Company': {
      color: '#EC4899',
      icon: 'file-alt',
      style: 'far',
    },
    Other: { color: '#6B7280', icon: 'ellipsis-h', style: 'fas' },
  }
  // Sorting state
  const [orderBy, setOrderBy] = useState('name')
  const [order, setOrder] = useState('asc')

  // Function to handle sort
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  // Function to create sortable header
  const createSortableHeader = (property, label) => (
    <TableSortLabel
      active={orderBy === property}
      direction={
        orderBy === property && order === 'desc' ? 'desc' : 'asc'
      }
      onClick={() => handleRequestSort(property)}
    >
      {label}
    </TableSortLabel>
  )

  // Function to get ranking color and label
  const getRankingDisplay = (ranking) => {
    const rankingConfig = {
      5: {
        color: '#4caf50',
        backgroundColor: '#e8f5e8',
        label: '5 - Excellent',
      },
      4: {
        color: '#8bc34a',
        backgroundColor: '#f1f8e9',
        label: '4 - Good',
      },
      3: {
        color: '#ff9800',
        backgroundColor: '#fff3e0',
        label: '3 - Average',
      },
      2: {
        color: '#f44336',
        backgroundColor: '#ffebee',
        label: '2 - Poor',
      },
      1: {
        color: '#d32f2f',
        backgroundColor: '#ffcdd2',
        label: '1 - Very Poor',
      },
    }

    // If ranking is null, undefined, or not a valid number, show "No Ranking"
    if (
      ranking === null ||
      ranking === undefined ||
      !rankingConfig[ranking]
    ) {
      return {
        color: '#ffebee',
        backgroundColor: '#f44336',
        label: 'No Ranking',
      }
    }

    return rankingConfig[ranking]
  }

  // Sort contacts based on current sort state
  const sortedContacts = useMemo(() => {
    return [...contacts].sort((a, b) => {
      let aValue = a[orderBy]
      let bValue = b[orderBy]

      // Handle special cases
      if (orderBy === 'types') {
        aValue = a.types?.join(', ') || ''
        bValue = b.types?.join(', ') || ''
      } else if (orderBy === 'ranking') {
        // Handle null rankings (treat as lowest)
        aValue = a.ranking || 0
        bValue = b.ranking || 0
      } else if (orderBy === 'email') {
        // Handle "No Email" chips
        aValue = a.email || 'No Email'
        bValue = b.email || 'No Email'
      } else if (orderBy === 'phone') {
        // Handle "No Phone" chips
        aValue = a.phone || 'No Phone'
        bValue = b.phone || 'No Phone'
      }

      // Convert to strings for comparison
      aValue = String(aValue || '').toLowerCase()
      bValue = String(bValue || '').toLowerCase()

      if (order === 'desc') {
        return bValue.localeCompare(aValue)
      }
      return aValue.localeCompare(bValue)
    })
  }, [contacts, orderBy, order])

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          p: '1.5rem',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Subtle scroll indicators */}
      {canScrollRight && (
        <Box
          sx={{
            position: 'absolute',
            top: 60,
            right: 4,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Tooltip title="Scroll right to see Actions column">
            <Box
              sx={{
                backgroundColor: 'rgba(59, 130, 246, 0.7)',
                color: 'white',
                borderRadius: '4px',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)',
                opacity: 0.8,
                '&:hover': {
                  opacity: 1,
                  backgroundColor: 'rgba(59, 130, 246, 0.9)',
                },
              }}
              onClick={() => {
                if (tableContainerRef.current) {
                  tableContainerRef.current.scrollBy({
                    left: 200,
                    behavior: 'smooth',
                  })
                }
              }}
            >
              <FontAwesomeIcon
                icon={['fas', 'arrow-right']}
                style={{ fontSize: '10px' }}
              />
            </Box>
          </Tooltip>
        </Box>
      )}

      {/* Left scroll indicator */}
      {canScrollLeft && (
        <Box
          sx={{
            position: 'absolute',
            top: 60,
            left: 4,
            zIndex: 10,
            backgroundColor: 'rgba(59, 130, 246, 0.7)',
            color: 'white',
            borderRadius: '4px',
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)',
            opacity: 0.8,
            '&:hover': {
              opacity: 1,
              backgroundColor: 'rgba(59, 130, 246, 0.9)',
            },
          }}
          onClick={() => {
            if (tableContainerRef.current) {
              tableContainerRef.current.scrollBy({
                left: -200,
                behavior: 'smooth',
              })
            }
          }}
        >
          <FontAwesomeIcon
            icon={['fas', 'arrow-left']}
            style={{ fontSize: '10px' }}
          />
        </Box>
      )}

      {/* Subtle shadow overlays */}
      {showLeftShadow && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 12,
            height: '100%',
            background:
              'linear-gradient(to right, rgba(0,0,0,0.05), transparent)',
            zIndex: 5,
            pointerEvents: 'none',
          }}
        />
      )}
      {showRightShadow && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 12,
            height: '100%',
            background:
              'linear-gradient(to left, rgba(0,0,0,0.05), transparent)',
            zIndex: 5,
            pointerEvents: 'none',
          }}
        />
      )}

      <TableContainer
        ref={tableContainerRef}
        sx={{
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#3B82F6',
            borderRadius: 4,
            '&:hover': {
              backgroundColor: '#2563eb',
            },
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                {createSortableHeader('state', 'State')}
              </TableCell>
              <TableCell>
                {createSortableHeader('types', 'Type')}
              </TableCell>
              <TableCell>
                {createSortableHeader('name', 'Name')}
              </TableCell>
              <TableCell>
                {createSortableHeader('email', 'Email')}
              </TableCell>
              <TableCell>
                {createSortableHeader('phone', 'Phone')}
              </TableCell>
              <TableCell>
                {createSortableHeader('address', 'Address')}
              </TableCell>
              <TableCell>
                {createSortableHeader('city', 'City')}
              </TableCell>
              <TableCell>
                {createSortableHeader('area', 'Area')}
              </TableCell>
              <TableCell>
                {createSortableHeader('ranking', 'Ranking')}
              </TableCell>
              <TableCell>
                {createSortableHeader('company', 'Company')}
              </TableCell>
              <TableCell>
                {createSortableHeader('notes', 'Notes')}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  backgroundColor: '#f8fafc',
                  borderLeft: '2px solid #3B82F6',
                  position: 'sticky',
                  right: 0,
                  zIndex: 1,
                  minWidth: 120,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1,
                  }}
                >
                  <FontAwesomeIcon
                    icon={['fas', 'cog']}
                    style={{ color: '#3B82F6', fontSize: '14px' }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#3B82F6',
                      fontWeight: 600,
                      fontSize: '14px',
                    }}
                  >
                    Actions
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedContacts.map((contact) => (
              <TableRow key={contact._id}>
                <TableCell>{contact.state}</TableCell>
                <TableCell>
                  {contact.types.map((type) => {
                    const cfg =
                      TYPE_VISUALS[type] || TYPE_VISUALS.Other
                    return (
                      <Chip
                        key={type}
                        label={
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            <FontAwesomeIcon
                              icon={[cfg.style, cfg.icon]}
                              style={{ fontSize: 12 }}
                            />
                            {type}
                          </span>
                        }
                        size="small"
                        style={{
                          color: 'white',
                          backgroundColor: cfg.color,
                          fontWeight: 600,
                        }}
                      />
                    )
                  })}
                </TableCell>
                <TableCell>
                  <div
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    {contact.image?.url_small ||
                    contact.image?.url ? (
                      <Avatar
                        src={
                          contact.image?.url_small ||
                          contact.image?.url
                        }
                        alt={contact.name}
                        sx={{ width: 32, height: 32, marginRight: 1 }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          marginRight: 1,
                          bgcolor: '#e0e0e0',
                          color: '#757575',
                          fontWeight: 'bold',
                          fontSize: 16,
                        }}
                      >
                        {contact.name
                          ? contact.name
                              .split(' ')
                              .map((n) => n[0])
                              .join('')
                              .toUpperCase()
                          : '?'}
                      </Avatar>
                    )}
                    <span style={{ fontWeight: 500 }}>
                      {contact.name}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {contact.email || (
                    <Chip
                      label="No Email"
                      size="small"
                      style={{
                        color: '#ffebee',
                        backgroundColor: '#f44336',
                        fontWeight: 'bold',
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {contact.phone || (
                    <Chip
                      label="No Phone"
                      size="small"
                      style={{
                        color: '#ffebee',
                        backgroundColor: '#f44336',
                        fontWeight: 'bold',
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>{contact.address}</TableCell>
                <TableCell>{contact.city}</TableCell>
                <TableCell>{contact.area}</TableCell>
                <TableCell>
                  <Chip
                    label={getRankingDisplay(contact.ranking).label}
                    size="small"
                    style={{
                      color: getRankingDisplay(contact.ranking).color,
                      backgroundColor: getRankingDisplay(
                        contact.ranking,
                      ).backgroundColor,
                      fontWeight: 'bold',
                    }}
                  />
                </TableCell>
                <TableCell>{contact.company}</TableCell>
                <TableCell>{contact.notes}</TableCell>
                <TableCell
                  align="right"
                  sx={{
                    backgroundColor: '#f8fafc',
                    borderLeft: '2px solid #3B82F6',
                    position: 'sticky',
                    right: 0,
                    zIndex: 1,
                    minWidth: 120,
                  }}
                >
                  {contact.types?.includes('Hard Money Lender') && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onQuestion(contact)}
                    >
                      <FontAwesomeIcon
                        icon={['far', 'circle-question']}
                      />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => onEdit(contact)}
                  >
                    <FontAwesomeIcon icon={['far', 'edit']} />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => onDelete(contact._id)}
                  >
                    <FontAwesomeIcon icon={['fas', 'trash']} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
